import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellRing, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Subscribe / Unsubscribe to push notifications for a single record.
 * Renders a compact pill button that toggles subscription state.
 *
 *  <SubscribeButton refType="sales_order" refId={order.id} label={order.voucher_no} />
 */
export default function SubscribeButton({ refType, refId, label = '', size = 'sm', className = '' }) {
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!refType || !refId) return;
      try {
        const res = await axios.get(`${API}/subscriptions/check`, {
          params: { ref_type: refType, ref_id: refId },
          withCredentials: true,
        });
        if (!cancelled) setSubscribed(!!res.data?.subscribed);
      } catch {} finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [refType, refId]);

  const toggle = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (pending) return;
    setPending(true);
    try {
      const url = subscribed ? `${API}/subscriptions/unsubscribe` : `${API}/subscriptions/subscribe`;
      const res = await axios.post(url, { ref_type: refType, ref_id: refId, label }, { withCredentials: true });
      setSubscribed(!!res.data?.subscribed);
      toast(subscribed
        ? 'Unsubscribed — you won\'t get updates for this anymore'
        : 'Subscribed — you\'ll get a push for every update on this',
        { description: label, duration: 3500 });
    } catch {
      toast.error('Could not update subscription');
    } finally { setPending(false); }
  };

  if (loading) return null;

  const sizeCls = size === 'xs'
    ? 'px-1.5 py-0.5 text-[10px] gap-1'
    : 'px-2 py-1 text-[11px] gap-1.5';

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={pending}
      data-testid={`subscribe-btn-${refType}-${refId}`}
      title={subscribed ? 'You\'re subscribed to updates. Click to unsubscribe.' : 'Subscribe to receive a push for every update on this record'}
      className={`inline-flex items-center ${sizeCls} rounded-lg border font-semibold transition-colors ${
        subscribed
          ? 'bg-amber-500/15 border-amber-500/40 text-amber-300 hover:bg-amber-500/25'
          : 'bg-zinc-800/60 border-zinc-700 text-zinc-400 hover:text-amber-300 hover:border-amber-500/40'
      } ${className}`}
    >
      {subscribed
        ? <><BellRing size={size === 'xs' ? 9 : 11} /> Subscribed <Check size={size === 'xs' ? 8 : 10} /></>
        : <><Bell size={size === 'xs' ? 9 : 11} /> Notify me</>}
    </button>
  );
}
